import { useState } from 'react'
import type { JSX } from 'react'

import {
  AlertDialog,
  Button,
  Flex,
  LockIcon,
  Notice,
  Switch,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useCurrentOrganization,
} from '~publish/contexts/AccountContext'

import { useUpdateOrganizationEnforce2FA } from './hooks/useUpdateOrganizationEnforce2FA'

export const SecuritySection = (): JSX.Element | null => {
  const { account } = useAccount()
  const currentOrganization = useCurrentOrganization()
  const { updateEnforce2FA, loading } = useUpdateOrganizationEnforce2FA()

  const organizationId = currentOrganization?.id
  const initialEnforce2FA = currentOrganization?.settings?.enforce2FA ?? false
  // The acting admin needs 2FA on their own account before turning enforcement
  // ON for the team — otherwise the server-side gate would lock them out on
  // their next request. The server enforces this via `ActorTwoFactorRequiredError`;
  // disabling the toggle here is a UX hint, not the authoritative gate.
  const actorHasTwoFactor = Boolean(account?.tfa?.type)

  const [enforce2FA, setEnforce2FA] = useState(initialEnforce2FA)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  if (!organizationId) return null

  // Disable the toggle when the admin can't yet turn enforcement ON. Once
  // enforcement is already ON, the admin must be able to turn it OFF even if
  // their own 2FA was somehow removed — disabling never triggers the lockout.
  const needsSelfTwoFactor = !enforce2FA && !actorHasTwoFactor
  const switchDisabled = loading || needsSelfTwoFactor
  const disabledTooltip = needsSelfTwoFactor
    ? 'Set up 2FA on your own account before requiring it for your team.'
    : null

  const apply = async (next: boolean): Promise<void> => {
    setEnforce2FA(next)
    try {
      await updateEnforce2FA({ organizationId, enforce2FA: next })
    } catch {
      setEnforce2FA(!next)
    }
  }

  const handleSwitchChange = (checked: boolean): void => {
    if (checked) {
      setIsConfirmOpen(true)
      return
    }
    void apply(false)
  }

  const handleConfirmEnable = (): void => {
    setIsConfirmOpen(false)
    void apply(true)
  }

  const switchControl = (
    <Switch
      aria-label="Require two-factor authentication for all team members"
      checked={enforce2FA}
      disabled={switchDisabled}
      onCheckedChange={handleSwitchChange}
    />
  )

  return (
    <>
      <Notice icon={<LockIcon />}>
        <Flex
          direction="row"
          justify="between"
          align="center"
          gap="md"
          fullWidth
        >
          <Flex direction="column" gap="xs">
            <Notice.Heading>Require Two-Factor Authentication</Notice.Heading>
            <Notice.Text>
              Every team member must have 2FA set up to use Buffer.
            </Notice.Text>
          </Flex>
          {disabledTooltip ? (
            <Tooltip content={disabledTooltip}>
              {/* Wrap in span so the disabled Switch still receives pointer events
                  for the tooltip — disabled Radix controls otherwise drop them. */}
              <span>{switchControl}</span>
            </Tooltip>
          ) : (
            switchControl
          )}
        </Flex>
      </Notice>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialog.Content>
          <AlertDialog.Title>Require 2FA for the Whole Team?</AlertDialog.Title>
          <AlertDialog.Description>
            Existing team members who don&apos;t have 2FA set up will be locked
            out of Buffer on their next request until they complete 2FA setup.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary">Cancel</Button>
            </AlertDialog.Cancel>
            <Button
              variant="primary"
              loading={loading}
              disabled={loading}
              onClick={handleConfirmEnable}
            >
              Require 2FA
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}
